import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { DataTableColumnMeta } from "@/components/shared/data-table/DataTable";
import { getDataTableColumnId } from "@/components/shared/data-table/data-table-columns";
import { readAllPages } from "@/lib/db/paged-read";
import { localDayKey } from "@/lib/format";
import { downloadBlob } from "@/lib/utils/download-blob";

/**
 * CSV export for the list tables.
 *
 * Two properties are the point of this module, and both are structural rather
 * than a rule someone has to remember:
 *
 * 1. It exports the *filtered set*, not the page in view. `fetchAllFilteredRows`
 *    is handed the same paged query the list runs and walks it to the end, so
 *    the file matches what the table says it is showing rather than the twenty
 *    rows that happen to be loaded.
 * 2. It exports the *visible columns*, in table order, using each column's
 *    declared `meta.exportValue`. A column with no `exportValue` — the
 *    selection checkbox, the actions menu — has no plain value and is skipped.
 */

/** Rows a single export may fetch before it refuses to continue. */
const MAX_EXPORT_ROWS = 20_000;

/** Rows per request while walking the filtered set. */
const EXPORT_PAGE_SIZE = 500;

/** RFC 4180 line ending: what spreadsheet software expects. */
const ROW_SEPARATOR = "\r\n";

/** Characters that force a field to be quoted: the delimiters and a quote. */
const CSV_RESERVED = /[",\r\n]/;

/**
 * A leading `=`, `+`, `-`, or `@` makes a spreadsheet execute the cell as a
 * formula; CSV quoting does not make it literal. Text fields can carry values
 * other users typed, so an unescaped export is a formula-injection vector into
 * the operator's spreadsheet session.
 */
const FORMULA_LEADING = /^[\t\r\n ]*[=+\-@]/;

export interface DataTableExportColumn<TData> {
  header: string;
  value: (row: TData) => string | number | null | undefined;
}

/**
 * The columns an export writes: visible, in declaration order, and able to
 * state a plain value for themselves.
 *
 * Visibility is read the way TanStack reads it — a column is visible unless its
 * id is explicitly `false` — so hiding a column in the table hides it in the
 * file too, with no second source of truth.
 */
export function exportableColumns<TData>(
  columns: ColumnDef<TData, unknown>[],
  columnVisibility: VisibilityState = {},
): DataTableExportColumn<TData>[] {
  const exported: DataTableExportColumn<TData>[] = [];

  for (const column of columns) {
    const meta = column.meta as DataTableColumnMeta<TData> | undefined;
    const exportValue = meta?.exportValue;
    if (!exportValue) {
      continue;
    }

    const id = getDataTableColumnId(column);
    if (!id || columnVisibility[id] === false) {
      continue;
    }

    exported.push({
      header: meta?.label ?? (typeof column.header === "string" ? column.header : id),
      value: exportValue,
    });
  }

  return exported;
}

/**
 * One CSV field.
 *
 * Quoting is applied when the value carries a delimiter, a quote, a line break,
 * or edge whitespace a reader would otherwise lose; inner quotes are doubled.
 * Notes fields routinely contain commas and quoted speech, so this is the
 * difference between a readable file and a shifted one.
 */
export function toCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }

  let text = String(value);

  // The apostrophe is the spreadsheet convention for "this is text". Numbers
  // never take it, so exported amounts stay machine-readable. It goes at the
  // very start of the cell, ahead of any leading whitespace, so the first
  // character a spreadsheet reads is the text marker rather than a space.
  if (typeof value === "string" && FORMULA_LEADING.test(text)) {
    text = `'${text}`;
  }

  const needsQuotes = CSV_RESERVED.test(text) || text.trim() !== text;
  if (!needsQuotes) {
    return text;
  }

  return `"${text.replaceAll('"', '""')}"`;
}

interface BuildCsvOptions<TData> {
  columns: ColumnDef<TData, unknown>[];
  columnVisibility?: VisibilityState;
  rows: TData[];
}

/** The CSV document for these rows under these columns. Header row always present. */
export function buildCsv<TData>({
  columns,
  columnVisibility,
  rows,
}: BuildCsvOptions<TData>): string {
  const exported = exportableColumns(columns, columnVisibility);
  const header = exported.map((column) => toCsvCell(column.header)).join(",");
  const body = rows.map((row) => exported.map((column) => toCsvCell(column.value(row))).join(","));

  return [header, ...body].join(ROW_SEPARATOR);
}

/**
 * Filename stem for anything a table emits, e.g. `invoices-open-2026-07-26`.
 *
 * The scope is in the name because the same table answers three different
 * questions depending on which chip is active, and a folder of files all
 * called `invoices.csv` cannot be told apart afterwards. Shared with the bulk
 * PDF archive so a CSV and a bundle of the same view sort together.
 */
export function dataTableExportStem(table: string, scope: string, today = new Date()): string {
  const date = localDayKey(today);
  const slug = [table, scope]
    .filter((part) => part.length > 0)
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return `${slug}-${date}`;
}

/** CSV export filename, e.g. `invoices-open-2026-07-26.csv`. */
export function dataTableExportFilename(table: string, scope: string, today = new Date()): string {
  return `${dataTableExportStem(table, scope, today)}.csv`;
}

/**
 * Walks a server-side paged query to the end of the filtered set.
 *
 * The datasets these lists hold top out in the low thousands, so a paged fetch
 * is the whole design — there is no case here for a streaming export. Past
 * `MAX_EXPORT_ROWS` it throws instead of returning what it has: a spreadsheet
 * that is silently missing rows is worse than no spreadsheet.
 *
 * `fetchPage` must return rows in a TOTAL order; the list clients get that from
 * `orderListPage` in `src/lib/db/list-order.ts`. Successive offsets over a
 * partial order are not successive slices of one sequence — rows in a tie group
 * spanning a page boundary arrive on both sides of it or on neither — and
 * nothing downstream can detect it, because the row count still looks right.
 */
export async function fetchAllFilteredRows<TData>(
  fetchPage: (params: { limit: number; offset: number }) => Promise<TData[]>,
  options?: { maxRows?: number; pageSize?: number },
): Promise<TData[]> {
  return readAllPages(fetchPage, {
    limitMessage: (maxRows) =>
      `Export is limited to ${maxRows.toLocaleString()} rows. Narrow the filters and try again.`,
    maxRows: options?.maxRows ?? MAX_EXPORT_ROWS,
    pageSize: options?.pageSize ?? EXPORT_PAGE_SIZE,
  });
}

/**
 * Hands the CSV to the browser as a download.
 *
 * The UTF-8 BOM is deliberate: without it Excel reads the file as the system
 * codepage and mangles every non-ASCII customer name.
 */
export function downloadCsv(filename: string, csv: string): void {
  downloadBlob(filename, new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }));
}
